import { ContextManager } from '../../context/manager';
import { mapDefinitions } from './mapDefinitions';
import { Entity, ExportHeaderSettings, Filters } from '../../types';
import { FilterSections, FilterType } from '../../types//content/filter.types';
import { adjustNumericFilter } from '../../filters/utils';

const getHeaderValues = (
  keys: { label: string; valueKey: string }[],
  contextValues: {
    values: Record<string, Entity[]>;
    definitions?: { name: string; type?: FilterType }[];
    sections?: FilterSections[];
  }
): [string, string][] => {
  const headerValues: [string, string][] = [];

  if (!keys || !contextValues) {
    return headerValues;
  }

  keys.forEach(key => {
    const definitions = contextValues.sections ? mapDefinitions(contextValues.sections) : contextValues.definitions;
    const settingDefinition = definitions.find(x => x.name === key.valueKey);
    const settingValue = contextValues.values[key.valueKey];
    if (!settingDefinition) {
      return;
    }

    const stringValue = !settingValue ? '' : settingValue.map(x => x.label).join(', ');
    const value = adjustNumericFilter(settingDefinition.type, stringValue);
    headerValues.push([key.label, value]);
  });

  return headerValues;
};

const getFiltersText = (filters: Filters, headerSettings: ExportHeaderSettings): string => {
  if (!filters) {
    return '';
  }
  const usedFilterKeys = headerSettings.filterValues.map(value => value.valueKey);
  const restFilterKeys = Object.keys(filters.values).filter(name => !usedFilterKeys.includes(name));
  const definitions = mapDefinitions(filters.sections);

  const restFilterKeysWithValues = restFilterKeys
    .filter(key => filters.values[key]?.length > 0)
    .map(key => ({
      valueKey: key,
      label: definitions.find(x => x.name === key)?.label,
    }))
    .filter(x => x.label !== undefined);

  const restFilterValues = getHeaderValues(restFilterKeysWithValues, filters);
  const filtersText = restFilterValues.map(values => `${values[0]}: ${values[1]}`).join(' | ');
  return filtersText;
};

export const getExportContext = (fileHeaderSettings: ExportHeaderSettings | null): [string, string][] => {
  if (!fileHeaderSettings) {
    return [];
  }

  const context = ContextManager.getInstance().get();
  const settings = getHeaderValues(fileHeaderSettings.settingValues, context.settings);
  const filters = getHeaderValues(fileHeaderSettings.filterValues, context.filters);
  const restFiltersText = getFiltersText(context.filters, fileHeaderSettings);
  const restFilters: [string, string] = ['Filters', restFiltersText];

  return [
    ...(fileHeaderSettings.customValues || []),
    ...(settings || []),
    ...(filters || []),
    ...(context.filters ? [restFilters] : []),
  ];
};
